import type {
  AlertSettingKey,
  IAlertSettingUpdate,
  IAlertSettingsResponse,
  IAlertsHubUpdate,
  IRestrictedAreaInput,
  IRestrictedAreaOptions,
} from '../../alert-kinds/index.js';
import { handle } from './errors.js';
import type { AuthedFetch } from './transport.js';

/**
 * Alert settings over an injected transport (docs/specs/2026-10-18-alerts-design.md):
 * the person's own, and the project's Alerts Hub. The alerts themselves —
 * viewed, dismissed — are on the notifications API, beside the bell's list.
 */
export function createAlertsApi(authedFetch: AuthedFetch) {
  const site = (siteId: string) => `/api/sites/${encodeURIComponent(siteId)}`;
  const hub = (siteId: string) => `${site(siteId)}/admin/alerts-hub`;

  async function json<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await authedFetch(path, init);
    if (!res.ok) return handle(res);
    return res.json() as Promise<T>;
  }

  const send = <T>(method: string, path: string, body?: unknown) =>
    json<T>(path, {
      method,
      ...(body !== undefined && { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
    });

  return {
    fetchPersonalAlertSettings: (siteId: string) => json<IAlertSettingsResponse>(`${site(siteId)}/alert-settings/me`),
    updatePersonalAlertSetting: (siteId: string, key: AlertSettingKey, update: IAlertSettingUpdate) =>
      send<{ ok: true }>('PUT', `${site(siteId)}/alert-settings/me/${encodeURIComponent(key)}`, update),

    fetchAlertsHub: (siteId: string) => json<IAlertSettingsResponse>(hub(siteId)),
    updateAlertsHub: (siteId: string, key: AlertSettingKey, update: IAlertsHubUpdate) =>
      send<{ ok: true }>('PUT', `${hub(siteId)}/${encodeURIComponent(key)}`, update),

    fetchRestrictedAreaOptions: (siteId: string) => json<IRestrictedAreaOptions>(`${hub(siteId)}/restricted-area-options`),
    createRestrictedArea: (siteId: string, input: IRestrictedAreaInput) =>
      send<{ id: string }>('POST', `${hub(siteId)}/restricted-areas`, input),
    updateRestrictedArea: (siteId: string, id: string, input: IRestrictedAreaInput) =>
      send<{ ok: true }>('PUT', `${hub(siteId)}/restricted-areas/${encodeURIComponent(id)}`, input),
    deleteRestrictedArea: (siteId: string, id: string) =>
      send<{ ok: true }>('DELETE', `${hub(siteId)}/restricted-areas/${encodeURIComponent(id)}`),
  };
}

export type AlertsApi = ReturnType<typeof createAlertsApi>;
